import './confetti-component.scss';
import { ConfettiMode } from '../../app/types';
import React, { useEffect } from 'react';
import confettiLib from 'canvas-confetti';

const confetti: typeof import('canvas-confetti') = confettiLib;

type VictoryConfettiProps = {
    mode: ConfettiMode | undefined;
    onDismiss?: () => void;
};

const emojiParticles = ['🎉', '🔥', '⭐️', '💥', '🚀'];

const fireEmojiConfetti = (confettiInstance: ReturnType<typeof confetti.create>) => {
    const emoji = emojiParticles[Math.floor(Math.random() * emojiParticles.length)];
    for (let i = 0; i < 5; i++) {
        confettiInstance({
            particleCount: 1,
            scalar: 1.45,
            spread: 360,
            ticks: 200,
            gravity: 0.6,
            origin: { x: Math.random(), y: Math.random() * 0.6 },
            shapes: ['circle'],
            colors: ['#ffffff'], // must provide at least one color
        });

        const el = document.createElement('div');
        el.innerText = emoji;
        el.style.position = 'fixed';
        el.style.fontSize = '1rem';
        el.style.left = `${Math.random() * 100}%`;
        el.style.top = `${Math.random() * 80}%`;
        el.style.pointerEvents = 'none';
        el.style.zIndex = '9999';
        el.style.opacity = '0';
        el.style.transition = 'opacity 0.3s ease-in';

        document.body.appendChild(el);
        setTimeout(() => { el.style.opacity = '1'; }, 10);
        setTimeout(() => { el.remove(); }, 2000);
    }
};

const colorPalettes: Record<ConfettiMode, string[]> = {
    duelLoss: ['#999'],
    duelWin: ['#ff0', '#0f0'],
    battleLoss: ['#aaa'],
    battleSmallWin: ['#4caf50', '#8bc34a', '#cddc39'],
    battleBigWin: ['#f44336', '#e91e63', '#9c27b0'],
    battleDraw: ['#ffc107', '#ff9800'],
    warLoss: ['#666'],
    warDraw: ['#ffc107', '#ff9800'],
    warSmallWin: ['#03a9f4', '#00bcd4', '#009688'],
    warBigWin: ['#ff5722', '#ffeb3b', '#cddc39']
};

const modeEffects: Record<ConfettiMode, ((instance: ReturnType<typeof confetti.create>) => void)[]> = {
    duelLoss: [],
    duelWin: [
        i => i({ particleCount: 20, spread: 70, origin: { y: 0.7 }, colors: colorPalettes.duelWin }),
        i => i({ particleCount: 30, spread: 80, origin: { x: 0.5, y: 0.6 }, colors: colorPalettes.duelWin }),
        i => i({ particleCount: 40, angle: 90, spread: 60, colors: colorPalettes.duelWin }),
        i => i({ particleCount: 25, ticks: 100, startVelocity: 20, colors: colorPalettes.duelWin }),
        i => i({ particleCount: 35, gravity: 0.3, colors: colorPalettes.duelWin }),
        i => i({ particleCount: 15, scalar: 2, colors: colorPalettes.duelWin }),
        i => i({ particleCount: 50, spread: 100, origin: { y: 0.2 }, colors: colorPalettes.duelWin }),
        i => i({ particleCount: 30, angle: 75, spread: 90, colors: colorPalettes.duelWin }),
        i => i({ particleCount: 28, startVelocity: 35, colors: colorPalettes.duelWin }),
        i => i({ particleCount: 60, spread: 60, colors: colorPalettes.duelWin }),
    ],
    battleLoss: [],
    battleDraw:[],
    battleSmallWin: [
        i => i({ particleCount: 40, spread: 90, colors: colorPalettes.battleSmallWin }),
        i => i({ particleCount: 60, angle: 60, spread: 100, colors: colorPalettes.battleSmallWin }),
        i => i({ particleCount: 70, angle: 120, spread: 100, colors: colorPalettes.battleSmallWin }),
        i => i({ particleCount: 50, origin: { y: 0.6 }, colors: colorPalettes.battleSmallWin }),
        i => i({ particleCount: 30, scalar: 1.2, colors: colorPalettes.battleSmallWin }),
        i => i({ particleCount: 65, spread: 80, colors: colorPalettes.battleSmallWin }),
        i => i({ particleCount: 75, angle: 100, spread: 140, colors: colorPalettes.battleSmallWin }),
        i => i({ particleCount: 80, origin: { y: 0.3 }, colors: colorPalettes.battleSmallWin }),
        i => i({ particleCount: 55, ticks: 120, colors: colorPalettes.battleSmallWin }),
        i => i({ particleCount: 60, gravity: 0.4, colors: colorPalettes.battleSmallWin }),
    ],
    battleBigWin: [
        i => fireEmojiConfetti(i),
        i => i({ particleCount: 100, spread: 150, colors: colorPalettes.battleBigWin }),
        i => i({ particleCount: 120, angle: 60, spread: 120, colors: colorPalettes.battleBigWin }),
        i => i({ particleCount: 120, angle: 120, spread: 120, colors: colorPalettes.battleBigWin }),
        i => i({ particleCount: 150, origin: { y: 0.4 }, colors: colorPalettes.battleBigWin }),
        i => i({ particleCount: 130, scalar: 1.6, colors: colorPalettes.battleBigWin }),
        i => i({ particleCount: 160, angle: 90, colors: colorPalettes.battleBigWin }),
        i => i({ particleCount: 100, ticks: 200, colors: colorPalettes.battleBigWin }),
        i => i({ particleCount: 140, gravity: 0.5, colors: colorPalettes.battleBigWin }),
        i => i({ particleCount: 110, spread: 170, colors: colorPalettes.battleBigWin }),
    ],
    warLoss: [],
    warDraw: [
        i => i({ particleCount: 50, spread: 100, colors: colorPalettes.warDraw }),
        i => i({ particleCount: 60, angle: 75, spread: 110, colors: colorPalettes.warDraw }),
        i => i({ particleCount: 55, origin: { y: 0.5 }, colors: colorPalettes.warDraw }),
        i => i({ particleCount: 65, scalar: 1.3, colors: colorPalettes.warDraw }),
        i => i({ particleCount: 45, ticks: 180, colors: colorPalettes.warDraw }),
        i => i({ particleCount: 70, gravity: 0.4, colors: colorPalettes.warDraw }),
        i => i({ particleCount: 60, spread: 80, colors: colorPalettes.warDraw }),
        i => i({ particleCount: 66, angle: 100, colors: colorPalettes.warDraw }),
        i => i({ particleCount: 48, startVelocity: 30, colors: colorPalettes.warDraw }),
        i => i({ particleCount: 52, spread: 95, colors: colorPalettes.warDraw }),
    ],
    warSmallWin: [
        i => i({ particleCount: 80, spread: 120, colors: colorPalettes.warSmallWin }),
        i => i({ particleCount: 90, angle: 90, spread: 130, colors: colorPalettes.warSmallWin }),
        i => i({ particleCount: 85, ticks: 150, colors: colorPalettes.warSmallWin }),
        i => i({ particleCount: 88, gravity: 0.5, colors: colorPalettes.warSmallWin }),
        i => i({ particleCount: 92, origin: { y: 0.4 }, colors: colorPalettes.warSmallWin }),
        i => i({ particleCount: 95, scalar: 1.4, colors: colorPalettes.warSmallWin }),
        i => i({ particleCount: 87, spread: 100, colors: colorPalettes.warSmallWin }),
        i => i({ particleCount: 93, angle: 105, colors: colorPalettes.warSmallWin }),
        i => i({ particleCount: 89, startVelocity: 40, colors: colorPalettes.warSmallWin }),
        i => i({ particleCount: 91, spread: 110, colors: colorPalettes.warSmallWin }),
    ],
    warBigWin: [
        i => fireEmojiConfetti(i),
        i => i({ particleCount: 160, spread: 180, colors: colorPalettes.warBigWin }),
        i => i({ particleCount: 180, angle: 60, spread: 140, colors: colorPalettes.warBigWin }),
        i => i({ particleCount: 170, angle: 120, spread: 140, colors: colorPalettes.warBigWin }),
        i => i({ particleCount: 190, origin: { y: 0.4 }, colors: colorPalettes.warBigWin }),
        i => i({ particleCount: 200, scalar: 1.7, colors: colorPalettes.warBigWin }),
        i => i({ particleCount: 210, angle: 90, colors: colorPalettes.warBigWin }),
        i => i({ particleCount: 195, ticks: 220, colors: colorPalettes.warBigWin }),
        i => i({ particleCount: 185, gravity: 0.6, colors: colorPalettes.warBigWin }),
        i => i({ particleCount: 175, spread: 190, colors: colorPalettes.warBigWin }),
    ],
};

export const VictoryConfetti: React.FC<VictoryConfettiProps> = ({ mode }) => {
    useEffect(() => {
        if (!mode || mode === ConfettiMode.duelLoss || mode === ConfettiMode.battleLoss || mode === ConfettiMode.warLoss) {
            return;
        }

        const canvas = document.createElement('canvas');
        canvas.style.position = 'fixed';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.pointerEvents = 'none';
        canvas.style.zIndex = '9999';
        document.body.appendChild(canvas);

        const instance = confetti.create(canvas, { resize: true });
        const effects = modeEffects[mode];

        const interval = setInterval(() => {
            const pick = effects[Math.floor(Math.random() * effects.length)];
            pick(instance);
        }, 350);


        const timeout = setTimeout(() => {
            clearInterval(interval);

            instance({
                particleCount: 100,
                angle: 90,
                spread: 20,
                gravity: 1,
                ticks: 200,
                origin: { y: 0.2 },
            });

            setTimeout(() => {
                canvas.remove();
            }, 2500); // give time to fall
        }, 2500);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
            canvas.remove();
        };
    }, [mode]);

    return null;
};